import React, { useEffect, useState } from 'react'
import { Card, Typography, Spin, Tag, Button, message, Input, Row, Col } from 'antd'
import { UserOutlined, DollarOutlined, WarningOutlined, CheckCircleOutlined, BellOutlined, CloseCircleOutlined, EditOutlined, TeamOutlined, TrophyOutlined } from '@ant-design/icons'
import { dashboardAPI, alertsAPI, phrasesAPI } from '../services/api'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

const { Title, Text } = Typography

const ACCENT = '#E0B34A'
const GREEN = '#52c41a'
const RED = '#ff4d4f'

function Dashboard() {
  const [stats, setStats] = useState(null)
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [phrase, setPhrase] = useState('')
  const [savedPhrase, setSavedPhrase] = useState('')
  const [editingPhrase, setEditingPhrase] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [statsRes, alertsRes, phraseRes] = await Promise.all([
        dashboardAPI.getStats(),
        alertsAPI.getAll(),
        phrasesAPI.getToday()
      ])
      setStats(statsRes.data)
      setAlerts(alertsRes.data)
      if (phraseRes.data.phrase) {
        setSavedPhrase(phraseRes.data.phrase)
        setPhrase(phraseRes.data.phrase)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const resolveAlert = async (id) => {
    try {
      await alertsAPI.resolve(id)
      setAlerts(alerts.filter(a => a.id !== id))
      message.success('Alerta resuelta')
    } catch (error) {
      message.error('Error')
    }
  }

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />

  const s = stats || {}
  const total = s.totalClients || 0
  const active = s.activeClients || 0
  const expired = s.expiredClients || 0
  const income = s.monthlyIncome || 0
  const attToday = s.todayAttendance || 0
  const totalEnrolled = s.totalEnrolled || 0
  const attPercent = totalEnrolled > 0 ? Math.round((attToday / totalEnrolled) * 100) : 0
  const activePercent = total > 0 ? Math.round((active / total) * 100) : 0
  const maxClass = Math.max(1, ...(s.popularClasses || []).map(c => c.enrolledCount))

  const cardBase = { borderRadius: 14, border: 'none', boxShadow: '0 4px 24px rgba(0,0,0,0.4)', background: '#171717' }

  return (
    <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
      {/* Encabezado */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 8 }}>
        <div>
          <Text style={{ color: ACCENT, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>Panel de administración</Text>
          <Title level={3} style={{ margin: '2px 0 0', color: '#ffffff', fontWeight: 800, letterSpacing: 0.5 }}>¡Hola, Profe! 👋</Title>
          <Text style={{ color: '#999', fontSize: 13 }}>Esto es lo que está pasando hoy.</Text>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <Tag color={ACCENT} style={{ borderRadius: 20, padding: '2px 12px', fontWeight: 700, margin: 0, fontSize: 12 }}>
            <TrophyOutlined /> {dayjs().format('DD MMMM YYYY')}
          </Tag>
          {alerts.length > 0 && (
            <Tag color="red" style={{ padding: '2px 12px', borderRadius: 20, margin: 0, fontSize: 12 }}>
              <BellOutlined /> {alerts.length} alerta{alerts.length > 1 ? 's' : ''}
            </Tag>
          )}
        </div>
      </div>

      {/* Indicadores */}
      <Row gutter={[10, 10]}>
        <Col xs={12} md={6}>
          <Card hoverable onClick={() => navigate('/clients')} bodyStyle={{ padding: 16 }} style={{ ...cardBase, cursor: 'pointer' }}>
            <Text style={{ color: '#aaa', fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
              <UserOutlined style={{ color: ACCENT }} /> Alumnas
            </Text>
            <Text style={{ color: '#fff', fontSize: 34, fontWeight: 800, lineHeight: 1.1, display: 'block', marginTop: 6 }}>{total}</Text>
            <Text style={{ color: s.newClientsThisMonth > 0 ? GREEN : '#777', fontSize: 11, display: 'block', marginTop: 2 }}>
              {s.newClientsThisMonth > 0 ? `+${s.newClientsThisMonth} este mes` : 'Sin altas este mes'}
            </Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card hoverable onClick={() => navigate('/clients')} bodyStyle={{ padding: 16 }} style={{ ...cardBase, cursor: 'pointer' }}>
            <Text style={{ color: '#aaa', fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
              <CheckCircleOutlined style={{ color: GREEN }} /> Activas
            </Text>
            <Text style={{ color: '#fff', fontSize: 34, fontWeight: 800, lineHeight: 1.1, display: 'block', marginTop: 6 }}>{active}</Text>
            <Text style={{ color: active === total && total > 0 ? GREEN : '#777', fontSize: 11, display: 'block', marginTop: 2 }}>
              <CheckCircleOutlined style={{ fontSize: 10 }} /> {activePercent}% activas
            </Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card hoverable onClick={() => navigate('/clients')} bodyStyle={{ padding: 16 }} style={{ ...cardBase, cursor: 'pointer' }}>
            <Text style={{ color: '#aaa', fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
              <WarningOutlined style={{ color: expired > 0 ? RED : GREEN }} /> Cuotas vencidas
            </Text>
            <Text style={{ color: expired > 0 ? RED : '#fff', fontSize: 34, fontWeight: 800, lineHeight: 1.1, display: 'block', marginTop: 6 }}>{expired}</Text>
            <Text style={{ color: expired > 0 ? RED : GREEN, fontSize: 11, display: 'block', marginTop: 2 }}>
              {expired > 0 ? `${expired} alumna(s) a regularizar` : 'Todo al día'}
            </Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card hoverable onClick={() => navigate('/payments')} bodyStyle={{ padding: 16 }} style={{ ...cardBase, cursor: 'pointer' }}>
            <Text style={{ color: '#aaa', fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
              <DollarOutlined style={{ color: ACCENT }} /> Ingresos del mes
            </Text>
            <Text style={{ color: '#fff', fontSize: 34, fontWeight: 800, lineHeight: 1.1, display: 'block', marginTop: 6 }}>${income.toLocaleString('es-AR')}</Text>
            <Text style={{ color: income > 0 ? ACCENT : '#777', fontSize: 11, display: 'block', marginTop: 2 }}>
              {income > 0 ? `${dayjs().format('MMMM')} acumulado` : 'Sin ingresos registrados'}
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Frase del día */}
      <Card style={{ ...cardBase, marginTop: 10, background: 'linear-gradient(135deg, rgba(224,179,74,0.12), #171717 60%)', borderLeft: `3px solid ${ACCENT}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Text style={{ color: ACCENT, fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
            <TrophyOutlined /> Frase del día
          </Text>
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setEditingPhrase(!editingPhrase)} style={{ color: '#ccc' }}>
            Editar frase
          </Button>
        </div>
        {!editingPhrase ? (
          <Text style={{ color: '#ffffff', fontSize: 17, lineHeight: 1.6, fontStyle: 'italic', display: 'block' }}>
            "{savedPhrase || 'No importa qué tan lento avances, mientras no te detengas.'}"
          </Text>
        ) : (
          <div>
            <Input.TextArea
              rows={2}
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder="Escribí la frase motivacional de hoy..."
              style={{ marginBottom: 8 }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="primary" size="small" style={{ background: ACCENT, borderColor: ACCENT, color: '#000', fontWeight: 700 }} onClick={async () => { try { await phrasesAPI.create(phrase); setSavedPhrase(phrase); setEditingPhrase(false); message.success('Frase guardada!') } catch(e) { message.error('Error') } }}>
                Guardar
              </Button>
              <Button size="small" onClick={() => setEditingPhrase(false)}>Cancelar</Button>
            </div>
          </div>
        )}
        <Text style={{ color: '#888', fontSize: 11, display: 'block', marginTop: 8 }}>Motivá a tus alumnas 💛</Text>
      </Card>

      {/* Asistencia + Clases */}
      <Row gutter={[10, 10]} style={{ marginTop: 10 }}>
        <Col xs={24} md={12}>
          <Card style={cardBase} onClick={() => navigate('/attendance')} bodyStyle={{ padding: 16, cursor: 'pointer' }}>
            <Text style={{ color: '#aaa', fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <TeamOutlined style={{ color: ACCENT }} /> Asistencia de hoy
            </Text>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <Text style={{ color: '#fff', fontSize: 34, fontWeight: 800, lineHeight: 1 }}>{attToday}</Text>
              <Text style={{ color: '#999', fontSize: 13 }}>{attToday === 1 ? 'alumna' : 'alumnas'}</Text>
              {totalEnrolled > 0 && <Text style={{ color: '#666', fontSize: 12, marginLeft: 'auto' }}>{attToday}/{totalEnrolled} inscritas</Text>}
            </div>
            {totalEnrolled > 0 ? (
              <div style={{ marginTop: 10, height: 8, borderRadius: 6, background: '#222', overflow: 'hidden' }}>
                <div style={{ width: `${attPercent}%`, height: '100%', borderRadius: 6, background: attPercent >= 50 ? GREEN : ACCENT, transition: 'width 0.5s' }} />
              </div>
            ) : (
              <Text style={{ color: '#777', fontSize: 12, display: 'block', marginTop: 6 }}>Todavía no hay asistencias registradas</Text>
            )}
            {totalEnrolled > 0 && <Text style={{ color: '#888', fontSize: 11, display: 'block', marginTop: 6 }}>{attPercent}% de asistencia hoy</Text>}
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card style={cardBase} bodyStyle={{ padding: 16 }}>
            <Text style={{ color: '#aaa', fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              <TrophyOutlined style={{ color: ACCENT }} /> Clases más elegidas
            </Text>
            {(s.popularClasses || []).length === 0 ? (
              <Text style={{ color: '#777', fontSize: 12 }}>Todavía no hay alumnas inscriptas en clases</Text>
            ) : (
              s.popularClasses.map(c => (
                <div key={c.name} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text style={{ color: '#ddd', fontSize: 13, fontWeight: 600 }}>{c.name}</Text>
                    <Text style={{ color: '#999', fontSize: 12 }}>{c.enrolledCount} {c.enrolledCount === 1 ? 'alumna' : 'alumnas'}</Text>
                  </div>
                  <div style={{ height: 8, borderRadius: 6, background: '#222', overflow: 'hidden' }}>
                    <div style={{ width: `${Math.round((c.enrolledCount / maxClass) * 100)}%`, height: '100%', borderRadius: 6, background: ACCENT, transition: 'width 0.5s' }} />
                  </div>
                </div>
              ))
            )}
          </Card>
        </Col>
      </Row>

      {alerts.length > 0 && (
        <Card title={<span><BellOutlined /> Alertas y Recordatorios</span>} style={{ ...cardBase, marginTop: 10 }}>
          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            {alerts.map(item => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #242424' }}>
                <div>
                  <Tag color={item.type === 'surcharge' ? 'red' : item.type === 'expiration' ? 'orange' : 'blue'} style={{ marginRight: 8 }}>
                    {item.type === 'surcharge' ? 'Recargo' : item.type === 'expiration' ? 'Vencimiento' : 'Recordatorio'}
                  </Tag>
                  <Text strong style={{ color: '#fff' }}>{item.Client ? `${item.Client.name} ${item.Client.lastName}` : 'Cliente'}</Text>
                  <Text style={{ color: '#999', fontSize: 12, display: 'block', marginTop: 2 }}>{item.message}</Text>
                </div>
                <Button type="text" icon={<CloseCircleOutlined />} onClick={() => resolveAlert(item.id)} />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}

export default Dashboard
